/**
 * FlightCheckoutPage — rendered at /checkout/flight/:tripId/:flightOptionId.
 *
 * Page is permanent and revisitable. Booking state lives directly on the
 * matching flight itinerary item (no separate FlightBooking doc); until the
 * user records the booking the item's `booking.status` is `'planned'`. After
 * submit, the same URL renders a read-only confirmation; "Update booking"
 * reopens the form, "Remove booking" resets the item's booking state.
 */

import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { BookingDetails } from '@elara/domain/models/schemas/itinerary-item-schemas';

import React, { useEffect, useState } from 'react';

import { getCurrencyOptions } from '@elara/client-core/lib/currency-utils';
import { useAppStates, useNavigate, useQueryParams, useRouteParams } from '#states/hooks.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '#ui/dialog.js';
import { GoogleFlightBookingOptions } from '#ui/GoogleFlightBookingOptions.js';
import { Button, FloatingInput, FloatingSelect, FloatingTextarea, Loader, SelectItem } from '#ui/index.js';
import { CheckoutLayout } from '#views/checkout/CheckoutLayout.js';
import { FlightBookingSummary } from '#views/checkout/FlightBookingSummary.js';

const CURRENCY_OPTIONS = getCurrencyOptions();

export function FlightCheckoutPage(): React.ReactNode {
  const params = useRouteParams() as { tripId?: string; flightOptionId?: string };
  const query = useQueryParams();
  const tripId = params.tripId;
  const flightOptionId = params.flightOptionId;
  const { flightCheckoutState } = useAppStates('flightCheckoutState');
  const { navigate } = useNavigate();
  // `?step=form` lets entry points (e.g. Bookings tab "Mark as booked") jump
  // straight into the Did-you-book form, skipping the provider list.
  const initialView: 'options' | 'form' = query.step === 'form' ? 'form' : 'options';

  useEffect(() => {
    if (!tripId || !flightOptionId) return;
    flightCheckoutState.open(tripId, flightOptionId);
    return () => flightCheckoutState.reset();
  }, [tripId, flightOptionId, flightCheckoutState]);

  if (!tripId || !flightOptionId) {
    return <div className="p-8 text-sm text-destructive">Missing checkout parameters.</div>;
  }
  if (!flightCheckoutState.isReady) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader />
          <p className="text-sm">Loading your flight…</p>
        </div>
      </div>
    );
  }

  const booking = flightCheckoutState.booking;
  const flightOption = flightCheckoutState.flightOption;
  // The traveler's own note. Saved to the itinerary item's `notes` — the same note the trip view
  // and the event detail render (and let them edit). `customerRequest` is the legacy home for it,
  // kept as a read fallback for bookings recorded before the move.
  const bookingNote = flightCheckoutState.item?.notes ?? booking?.customerRequest ?? undefined;

  return (
    <CheckoutLayout
      summaryTotal={flightOption?.formattedTotalPrice}
      form={
        <FlightCheckoutForm
          booking={booking}
          bookingNote={bookingNote}
          flightOption={flightOption}
          tripId={tripId}
          initialView={initialView}
          onCleared={() => navigate(`/checkout/flight/${tripId}/${flightOptionId}`, { replace: true })}
          onRecorded={() => navigate(`/trip/${tripId}/bookings`)}
        />
      }
      summary={<FlightBookingSummary flightOption={flightOption} booking={booking} />}
    />
  );
}

function FlightCheckoutForm({
  booking,
  bookingNote,
  flightOption,
  tripId,
  initialView = 'options',
  onCleared,
  onRecorded,
}: {
  booking: BookingDetails | undefined;
  bookingNote: string | undefined;
  flightOption: FlightOptionModel | undefined;
  tripId: string;
  initialView?: 'options' | 'form';
  onCleared: () => void;
  onRecorded: () => void;
}): React.ReactNode {
  // The page swaps between the provider list and the "Did you book?" form.
  // Default to the form whenever a booking exists, or when the caller
  // explicitly requested the form view (e.g. Bookings tab "Mark as
  // booked"). Clicking a provider switches to the form and remembers which
  // provider for pre-fill. The form's "Show booking options" link switches back.
  const [view, setView] = useState<'options' | 'form'>(
    booking?.status === 'booked' || initialView === 'form' ? 'form' : 'options',
  );
  const [clickedProvider, setClickedProvider] = useState<string | undefined>(undefined);

  if (view === 'form') {
    return (
      <DidYouBookPanel
        booking={booking}
        bookingNote={bookingNote}
        flightOption={flightOption}
        tripId={tripId}
        onCleared={onCleared}
        onRecorded={onRecorded}
        providerHint={clickedProvider}
        onShowOptions={() => setView('options')}
      />
    );
  }

  return (
    <section>
      <h2 className="text-xl md:text-2xl font-display">Where to book</h2>
      <p className="mt-1.5 text-sm text-pretty text-muted-foreground">
        Pick a provider below to open their booking page in a new tab. Once you’ve booked, come back and tell us your
        confirmation number.
      </p>
      <div className="mt-8">
        {flightOption ? (
          <GoogleFlightBookingOptions
            flightOption={flightOption}
            onProviderClicked={(name) => {
              setClickedProvider(name);
              setView('form');
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

function DidYouBookPanel({
  booking,
  bookingNote,
  flightOption,
  tripId,
  onCleared,
  onRecorded,
  providerHint,
  onShowOptions,
}: {
  booking: BookingDetails | undefined;
  bookingNote: string | undefined;
  flightOption: FlightOptionModel | undefined;
  tripId: string;
  onCleared: () => void;
  onRecorded: () => void;
  /** Most-recently-clicked provider name, used to pre-fill the form. */
  providerHint?: string;
  /** Switches the page back to the booking-options view. */
  onShowOptions: () => void;
}): React.ReactNode {
  const { flightCheckoutState } = useAppStates('flightCheckoutState');
  const [editing, setEditing] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState(booking?.confirmationNumber ?? '');
  const [notes, setNotes] = useState(bookingNote ?? '');
  const [providerName, setProviderName] = useState(booking?.bookedProvider?.name ?? providerHint ?? '');
  // Pre-fill the actual price from the FlightOption's listed price; user can edit.
  const [priceAmount, setPriceAmount] = useState<string>(
    flightOption?.cost?.amount != null ? String(flightOption.cost.amount) : '',
  );
  const [priceCurrency, setPriceCurrency] = useState<string>(flightOption?.cost?.currency ?? 'USD');
  const [error, setError] = useState<string | undefined>(undefined);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  // When the booking shape changes (server update), refresh the local form fields.
  useEffect(() => {
    setConfirmationNumber(booking?.confirmationNumber ?? '');
    setNotes(bookingNote ?? '');
    setProviderName(booking?.bookedProvider?.name ?? providerHint ?? '');
  }, [booking?.confirmationNumber, bookingNote, booking?.bookedProvider?.name, providerHint]);

  // Re-seed the price fields when the FlightOption's cost loads/changes.
  useEffect(() => {
    if (flightOption?.cost?.amount != null) setPriceAmount(String(flightOption.cost.amount));
    if (flightOption?.cost?.currency) setPriceCurrency(flightOption.cost.currency);
  }, [flightOption?.cost?.amount, flightOption?.cost?.currency]);

  async function handleSave() {
    if (!confirmationNumber.trim()) {
      setError('Please enter a confirmation number.');
      return;
    }
    const parsedAmount = priceAmount.trim() === '' ? undefined : Number(priceAmount);
    if (parsedAmount != null && (Number.isNaN(parsedAmount) || parsedAmount < 0)) {
      setError('Final price must be a non-negative number.');
      return;
    }
    setError(undefined);
    try {
      await flightCheckoutState.submit({
        confirmationNumber: confirmationNumber.trim(),
        // Always sent (even empty) — this field owns the item's note, so clearing it here clears it.
        notes: notes.trim(),
        bookedProvider: providerName.trim() ? { name: providerName.trim() } : undefined,
        actualCost:
          parsedAmount != null && priceCurrency.trim()
            ? { amount: parsedAmount, currency: priceCurrency.trim().toUpperCase() }
            : undefined,
      });
      setEditing(false);
      onRecorded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleConfirmRemove() {
    setRemoving(true);
    try {
      await flightCheckoutState.clear();
      setConfirmRemoveOpen(false);
      onCleared();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove booking');
    } finally {
      setRemoving(false);
    }
  }

  // Booked confirmation view — same shape as the "Where to book" section
  // (font-display heading, muted description, no border/padding).
  if (booking?.status === 'booked' && !editing) {
    return (
      <section>
        <h2 className="text-xl md:text-2xl font-display">You’ve booked this flight</h2>
        <p className="mt-1.5 text-sm text-pretty text-muted-foreground">
          Your details are saved. We’ll keep this in sync with the rest of your trip.
        </p>

        <dl className="mt-8 space-y-3 text-sm">
          <div className="flex gap-2">
            <dt className="w-32 text-muted-foreground">Confirmation #</dt>
            <dd className="font-medium">{booking.confirmationNumber}</dd>
          </div>
          {booking.bookedProvider?.name ? (
            <div className="flex gap-2">
              <dt className="w-32 text-muted-foreground">Provider</dt>
              <dd>{booking.bookedProvider.name}</dd>
            </div>
          ) : null}
          {bookingNote ? (
            <div className="flex gap-2">
              <dt className="w-32 text-muted-foreground">Notes</dt>
              <dd>{bookingNote}</dd>
            </div>
          ) : null}
        </dl>

        {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setEditing(true)}>
            Update booking
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirmRemoveOpen(true)}>
            Remove booking
          </Button>
        </div>

        <Dialog open={confirmRemoveOpen} onOpenChange={(open) => !removing && setConfirmRemoveOpen(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Remove booking?</DialogTitle>
              <DialogDescription>Any saved booking details for this flight will be lost.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" disabled={removing} onClick={() => setConfirmRemoveOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" disabled={removing} onClick={() => void handleConfirmRemove()}>
                {removing ? 'Removing…' : 'Remove booking'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    );
  }

  // Awaiting + form OR booked + editing.
  return (
    <section>
      <h2 className="text-xl md:text-2xl font-display">Did you book?</h2>
      <p className="mt-1.5 text-sm text-pretty text-muted-foreground">
        Once you've completed your booking, save your details here so we can keep your trip in sync.
      </p>

      <div className="mt-8 grid gap-4">
        <FloatingInput
          id="confirmationNumber"
          label="Confirmation number"
          required
          value={confirmationNumber}
          onChange={(e) => setConfirmationNumber(e.target.value)}
          autoComplete="off"
        />
        <FloatingInput
          id="bookedProvider"
          label="Where did you book?"
          value={providerName}
          onChange={(e) => setProviderName(e.target.value)}
        />
        <div>
          <div className="grid grid-cols-2 gap-2">
            <FloatingInput
              id="actualPriceAmount"
              label="Final price"
              value={priceAmount}
              onChange={(e) => setPriceAmount(e.target.value)}
              autoComplete="off"
              inputMode="decimal"
            />
            <FloatingSelect
              id="actualPriceCurrency"
              label="Currency"
              value={priceCurrency}
              onValueChange={(value) => setPriceCurrency(value)}
            >
              {CURRENCY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </FloatingSelect>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            What you actually paid. This keeps your trip costings accurate.
          </p>
        </div>
        <FloatingTextarea
          id="notes"
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
        />
      </div>

      {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Button onClick={() => void handleSave()} isBusy={flightCheckoutState.isSubmitting}>
          {booking?.status === 'booked' ? 'Save changes' : 'Save booking'}
        </Button>
        {booking?.status === 'booked' ? (
          <Button variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        ) : null}
        {booking?.status !== 'booked' && (
          <Button variant="ghost" onClick={onShowOptions}>
            Show booking options
          </Button>
        )}
      </div>
    </section>
  );
}
